/**
 * Type guards and safe casting utilities for workflow parser.
 * Provides type-safe narrowing with graceful error handling.
 */

const LOG_PREFIX = '[Workflow Parser]';

function logWarn(message: string): void {
    if (process.env.NODE_ENV !== 'production') {
        console.warn(`${LOG_PREFIX} ${message}`);
    }
}

/**
 * True for any non-null object, including arrays and Dates.
 * For JSON-like key/value objects only, use {@link isPlainRecord}.
 */
export function isObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object';
}

/** Plain object (not array, not null) — typical JSON object from server config. */
export function isPlainRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// ---------------------------------------------------------------------------
// Silent casts (canonical boundary for server `unknown` → typed values)
// Prefer these over ad hoc `typeof` at the parser boundary.
// ---------------------------------------------------------------------------

export function castToString(value: unknown): string | undefined {
    return isString(value) ? value : undefined;
}

export function castToNumber(value: unknown): number | undefined {
    return isNumber(value) ? value : undefined;
}

export function castToBoolean(value: unknown): boolean | undefined {
    return isBoolean(value) ? value : undefined;
}

export function castToStringArray(value: unknown): string[] {
    return isArray(value, isString) ? value : [];
}

export function isString(value: unknown): value is string {
    return typeof value === 'string';
}

export function isNumber(value: unknown): value is number {
    return typeof value === 'number' && !Number.isNaN(value);
}

export function isBoolean(value: unknown): value is boolean {
    return typeof value === 'boolean';
}

export function isArray<T>(value: unknown, itemGuard?: (item: unknown) => item is T): value is T[] {
    if (!Array.isArray(value)) return false;
    if (itemGuard) return value.every(itemGuard);
    return true;
}

export function safeCastString(value: unknown, field?: string): string | undefined {
    if (isString(value)) return value;
    if (value != null && field) {
        logWarn(`Field "${field}": expected string, got ${typeof value}`);
    }
    return undefined;
}

export function safeCastNumber(value: unknown, field?: string): number | undefined {
    if (isNumber(value)) return value;
    if (value != null && field) {
        logWarn(`Field "${field}": expected number, got ${typeof value}`);
    }
    return undefined;
}

export function safeCastBoolean(value: unknown, field?: string): boolean | undefined {
    if (isBoolean(value)) return value;
    if (value != null && field) {
        logWarn(`Field "${field}": expected boolean, got ${typeof value}`);
    }
    return undefined;
}

export function safeCastArray<T>(
    value: unknown,
    itemGuard: (item: unknown) => item is T = isString as (item: unknown) => item is T,
    field?: string
): T[] {
    if (isArray(value, itemGuard)) return value;
    if (value != null && field) {
        logWarn(`Field "${field}": expected array, got ${typeof value}`);
    }
    return [];
}

export function safeCastObject(value: unknown, field?: string): Record<string, unknown> | undefined {
    if (isObject(value)) return value;
    if (value != null && field) {
        logWarn(`Field "${field}": expected object, got ${typeof value}`);
    }
    return undefined;
}
